import { __decorate, __metadata } from "tslib";
import { changedAttribute, elementDefine, eventShadow, eventWindow, mutationObserverLight, onConnectedAfter, onConnectedBodyShadow, onDisconnected, queryShadow, resizeObserverLight } from "@dooboostore/simple-web-component";
import { computeMacdSeries, computeRsiSeries, computeObvSeries, computeSmaSeries, computeEmaSeries } from "@dooboostore/algorithm";
const tagName = "stock-chart";
function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}
function fmtInd(v) {
  const a = Math.abs(v);
  if (a >= 1e3)
    return Math.round(v).toLocaleString();
  if (a >= 100)
    return v.toFixed(1);
  if (a >= 1)
    return v.toFixed(2);
  if (a === 0)
    return "0";
  return v.toFixed(3);
}
function fmtBig(v) {
  const a = Math.abs(v);
  const sign = v < 0 ? "-" : "";
  if (a >= 1e8)
    return `${sign}${(a / 1e8).toFixed(1)}\uC5B5`;
  if (a >= 1e4)
    return `${sign}${Math.round(a / 1e4).toLocaleString()}\uB9CC`;
  return Math.round(v).toLocaleString();
}
function maSeriesKey(ma) {
  return `${ma.type}:${ma.period}`;
}
function buildCandleInfo(index, points, mas, sma, macdCfg, macd, rsiCfg, rsi, obvCfg, obv) {
  const d = points[index];
  if (!d)
    return null;
  const maSlopeOf = (arr) => {
    const v = arr?.[index] ?? null;
    const pv = arr?.[index - 1] ?? null;
    return v != null && pv != null && pv !== 0 ? (v - pv) / pv * 100 : null;
  };
  return {
    index,
    candle: { date: d.date, open: d.open, high: d.high, low: d.low, close: d.close, volume: d.volume },
    ma: mas.map((m) => {
      const arr = sma.get(maSeriesKey(m));
      return { period: m.period, color: m.color, value: arr?.[index] ?? null, slope: maSlopeOf(arr) };
    }),
    macd: macdCfg && macd ? { fast: macdCfg.fast, slow: macdCfg.slow, signalPeriod: macdCfg.signal, value: macd.macd[index], signal: macd.signal[index], hist: macd.hist[index], histSlope: index > 0 ? macd.hist[index] - macd.hist[index - 1] : null } : null,
    rsi: rsiCfg && rsi ? { period: rsiCfg.period, value: rsi[index] } : null,
    obv: obvCfg && obv ? { value: obv[index] } : null
  };
}
var StockChart_default = (w) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _j;
  const existing = w.customElements.get(tagName);
  if (existing)
    return existing;
  let StockChartImpl = class StockChartImpl extends w.HTMLElement {
    constructor() {
      super(...arguments);
      Object.defineProperty(this, "points", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: []
      });
      Object.defineProperty(this, "shapes", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: []
      });
      Object.defineProperty(this, "mas", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: []
      });
      Object.defineProperty(this, "lineSeries", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: []
      });
      Object.defineProperty(this, "viewStart", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: 0
      });
      Object.defineProperty(this, "viewEnd", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: 0
      });
      Object.defineProperty(this, "selectedIdx", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: -1
      });
      Object.defineProperty(this, "viewInitDone", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: false
      });
      Object.defineProperty(this, "lastPointsLength", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: -1
      });
      Object.defineProperty(this, "showCloseLine", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: false
      });
      Object.defineProperty(this, "showVolume", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: false
      });
      Object.defineProperty(this, "macd", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: null
      });
      Object.defineProperty(this, "macdCache", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: null
      });
      Object.defineProperty(this, "rsi", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: null
      });
      Object.defineProperty(this, "rsiCache", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: null
      });
      Object.defineProperty(this, "obv", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: null
      });
      Object.defineProperty(this, "obvCache", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: null
      });
      Object.defineProperty(this, "seriesDirty", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: true
      });
      Object.defineProperty(this, "series", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: null
      });
      Object.defineProperty(this, "showMinMax", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: false
      });
      Object.defineProperty(this, "showLastLine", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: false
      });
      Object.defineProperty(this, "controlsEnabled", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: false
      });
      Object.defineProperty(this, "readoutEnabled", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: false
      });
      Object.defineProperty(this, "hiddenXLabel", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: false
      });
      Object.defineProperty(this, "edgeFired", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: { start: false, end: false }
      });
      Object.defineProperty(this, "hiddenYLabel", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: false
      });
      Object.defineProperty(this, "disabledEvent", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: false
      });
      Object.defineProperty(this, "dragLastX", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: 0
      });
      Object.defineProperty(this, "dragging", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: false
      });
      Object.defineProperty(this, "downX", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: 0
      });
      Object.defineProperty(this, "downY", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: 0
      });
      Object.defineProperty(this, "pinchDist0", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: 0
      });
      Object.defineProperty(this, "pinchSpan0", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: 0
      });
      Object.defineProperty(this, "pinchAnchorFrac", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: 0.5
      });
      Object.defineProperty(this, "pinchViewStart0", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: 0
      });
      Object.defineProperty(this, "padL", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: 6
      });
      Object.defineProperty(this, "padR", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: 58
      });
      Object.defineProperty(this, "lastTouchEndTime", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: 0
      });
      Object.defineProperty(this, "chartCanvas", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: void 0
      });
      Object.defineProperty(this, "readoutEl", {
        enumerable: true,
        configurable: true,
        writable: true,
        value: void 0
      });
    }
    // show-close-line 속성 변경 시 호출 (기본값: 안 보임)
    onShowCloseLineChanged(value) {
      this.showCloseLine = !!value;
      if (this.chartCanvas)
        this.drawChart();
    }
    // 자식 <volume> 요소 — 있으면 거래량 영역 표시 (기본값: 숨김)
    collectVolume() {
      this.showVolume = !!this.querySelector(":scope > volume");
    }
    // 자식 <macd> 요소 — 있으면 MACD 영역 표시 (기본값: 숨김)
    // <macd><fast period="12" color=".."/><slow period="26"/><signal period="9" color=".."/></macd>
    // slow는 period만 사용 (슬로우선은 그리지 않음)
    collectMacd() {
      const el = this.querySelector(":scope > macd");
      if (!el) {
        this.macd = null;
        this.seriesDirty = true;
        return;
      }
      const attr = (name, an, fb) => el.querySelector(`:scope > ${name}`)?.getAttribute(an) || fb;
      const per = (name, fb) => Math.max(2, Math.round(Number(attr(name, "period", String(fb))) || fb));
      const fast = per("fast", 12);
      let slow = per("slow", 26);
      if (slow <= fast)
        slow = fast + 1;
      this.macd = {
        fast,
        slow,
        signal: per("signal", 9),
        fastColor: attr("fast", "color", "#e5484d"),
        signalColor: attr("signal", "color", "#f59e0b")
      };
      this.seriesDirty = true;
    }
    // 자식 <rsi> 요소 — 있으면 RSI 영역 표시 (기본값: 숨김)
    // <rsi period="14"><line color=".."/><overbought level="70" color=".."/><oversold level="30" color=".."/></rsi>
    collectRsi() {
      const el = this.querySelector(":scope > rsi");
      if (!el) {
        this.rsi = null;
        this.seriesDirty = true;
        return;
      }
      const child = (name) => el.querySelector(`:scope > ${name}`);
      const lv = (name, fb) => {
        const c = child(name);
        const raw = c ? c.getAttribute("level") ?? c.getAttribute("value") : null;
        const v = Number(raw);
        return raw != null && Number.isFinite(v) && v > 0 && v < 100 ? v : fb;
      };
      let ob = lv("overbought", 70);
      let os = lv("oversold", 30);
      if (ob <= os) {
        ob = 70;
        os = 30;
      }
      this.rsi = {
        period: Math.max(2, Math.round(Number(el.getAttribute("period")) || 14)),
        lineColor: child("line")?.getAttribute("color") || "#7c3aed",
        overbought: { level: ob, color: child("overbought")?.getAttribute("color") || "#e5484d" },
        oversold: { level: os, color: child("oversold")?.getAttribute("color") || "#3e63dd" }
      };
      this.seriesDirty = true;
    }
    // 자식 <obv> 요소 — 있으면 OBV 영역 표시 (기본값: 숨김)
    // <obv><line color=".."/></obv>
    collectObv() {
      const el = this.querySelector(":scope > obv");
      if (!el) {
        this.obv = null;
        this.seriesDirty = true;
        return;
      }
      this.obv = {
        lineColor: el.querySelector(":scope > line")?.getAttribute("color") || "#0ea5e9"
      };
      this.seriesDirty = true;
    }
    // show-min-max 속성 — true면 최고/최저 툴팁 표시 (기본값: 안 보임)
    onShowMinMaxChanged(value) {
      this.showMinMax = !!value;
      if (this.chartCanvas)
        this.drawChart();
    }
    // show-last-line 속성 — true면 마지막 종가 가로점선 + 태그 표시 (기본값: 안 보임)
    onShowLastLineChanged(value) {
      this.showLastLine = !!value;
      if (this.chartCanvas)
        this.drawChart();
    }
    // enabled-control 속성 — true면 드래그 이동/휠 줌/핀치 등 제어 활성화 (기본값: 비활성)
    onEnabledControlChanged(value) {
      this.controlsEnabled = !!value;
    }
    // enabled-readout 속성 — true면 클릭/탭 시 캔들 정보(리드아웃) 표시 (기본값: 비활성)
    onEnabledReadoutChanged(value) {
      this.readoutEnabled = !!value;
    }
    // hidden-x-label — true면 x축(날짜) 라벨 숨김 (기본값: 보임)
    onHiddenXLabelChanged(value) {
      this.hiddenXLabel = !!value;
      if (this.chartCanvas)
        this.drawChart();
    }
    // hidden-y-label — true면 y축(가격/거래량 수치) 라벨 숨김 (기본값: 보임)
    onHiddenYLabelChanged(value) {
      this.hiddenYLabel = !!value;
      if (this.chartCanvas)
        this.drawChart();
    }
    // disabled-event 속성 — true면 모든 포인터/터치/휠 이벤트 리스너를 무시 (기본값: 활성)
    onDisabledEventChanged(value) {
      this.disabledEvent = !!value;
    }
    // ---------- public ----------
    setData(points) {
      this.points = points || [];
      this.viewInitDone = false;
      this.edgeFired = { start: false, end: false };
      this.selectedIdx = -1;
      this.seriesDirty = true;
      if (this.isConnected) {
        this.setupChart();
      }
    }
    setView(start, end) {
      const n = this.points.length;
      if (!n)
        return;
      const s = Math.max(0, Math.min(Math.floor(start), n - 1));
      const e = Math.max(s, Math.min(Math.ceil(end), n - 1));
      this.viewStart = s;
      this.viewEnd = e;
      this.viewInitDone = true;
      if (this.isConnected) {
        this.drawChart();
      }
    }
    /** 현재 뷰 구간 조회 (없으면 null) — 예측 꼬리분 뷰 확장용 */
    getView() {
      if (!this.points.length)
        return null;
      return { start: Math.floor(this.viewStart), end: Math.ceil(this.viewEnd) };
    }
    /** 줌 해제 — 전체 구간 표시 (다음 틱 수집 때도 전체로 초기화) */
    resetView() {
      const n = this.points.length;
      this.viewStart = 0;
      this.viewEnd = Math.max(0, n - 1);
      this.viewInitDone = false;
      if (this.isConnected && n) {
        this.drawChart();
      }
    }
    getCandleInfo(key) {
      const idx = typeof key === "number" ? Number.isInteger(key) ? key : -1 : this.points.findIndex((d) => d.date === key);
      if (idx < 0 || idx >= this.points.length)
        return null;
      const s = this.ensureSeries();
      return buildCandleInfo(idx, this.points, this.mas, s.sma, this.macd, s.macd, this.rsi, s.rsi, this.obv, s.obv);
    }
    getCandleInfos(keys) {
      const list = keys ?? this.points.map((_, i) => i);
      const out = [];
      for (const k of list) {
        const info = this.getCandleInfo(k);
        if (info)
          out.push(info);
      }
      return out;
    }
    getCandleInfoByName(name) {
      let idx = -1;
      this.querySelectorAll(":scope > candle").forEach((el, i) => {
        if (idx >= 0)
          return;
        if (el.getAttribute("name") === name || el.getAttribute("date") === name)
          idx = i;
      });
      if (idx < 0 || idx >= this.points.length)
        idx = this.points.findIndex((d) => d.date === name);
      if (idx < 0 || idx >= this.points.length)
        return null;
      const s = this.ensureSeries();
      return buildCandleInfo(idx, this.points, this.mas, s.sma, this.macd, s.macd, this.rsi, s.rsi, this.obv, s.obv);
    }
    getCandleInfoByNames(names) {
      if (names == null)
        return this.getCandleInfos();
      const out = [];
      for (const nm of names) {
        const info = this.getCandleInfoByName(nm);
        if (info)
          out.push(info);
      }
      return out;
    }
    // ---------- 지표 시리즈 (그리기·조회 공용 캐시) ----------
    ensureSeries() {
      if (!this.seriesDirty && this.series)
        return this.series;
      const closes = this.points.map((p) => p.close);
      const sma = /* @__PURE__ */ new Map();
      for (const ma of this.mas) {
        const key = maSeriesKey(ma);
        if (!sma.has(key))
          sma.set(key, ma.type === "ema" ? computeEmaSeries(closes, ma.period) : computeSmaSeries(closes, ma.period));
      }
      this.series = {
        sma,
        macd: this.macd ? computeMacdSeries(closes, this.macd.fast, this.macd.slow, this.macd.signal) : null,
        rsi: this.rsi ? computeRsiSeries(closes, this.rsi.period) : null,
        obv: this.obv ? computeObvSeries(closes, this.points.map((p) => p.volume)) : null
      };
      this.seriesDirty = false;
      return this.series;
    }
    // 자식 <candle>에 .value 주입 — 속성 변경이 아니라 감시 루프 없음
    stampCandleValues() {
      const s = this.ensureSeries();
      this.querySelectorAll(":scope > candle").forEach((el, i) => {
        el.value = buildCandleInfo(i, this.points, this.mas, s.sma, this.macd, s.macd, this.rsi, s.rsi, this.obv, s.obv);
      });
    }
    // ---------- 렌더 ----------
    render() {
      return `
        <style>
          :host { display: block; position: relative; }
          #stock-chart-canvas {
            display: block; width: 100%; height: 100%;
            touch-action: pan-y; cursor: crosshair;
          }
          :host([enabled-control]) #stock-chart-canvas {
            touch-action: pan-y;
          }
          .chart-readout {
            position: absolute; top: 8px; left: 10px;
            display: none; flex-wrap: wrap; gap: 8px;
            font-size: 11px; color: #555;
            background: rgba(255,255,255,0.92); border: 1px solid #eef0f4;
            border-radius: 6px; padding: 3px 9px;
            pointer-events: none; z-index: 2;
          }
        </style>
        <canvas id="stock-chart-canvas"></canvas>
        <div class="chart-readout" id="stock-chart-readout"></div>
      `;
    }
    // ---------- 라이프사이클 ----------
    onConnected() {
      this.collectFromCandles();
      this.collectFromShapes();
      this.collectMas();
      this.collectLineSeries();
      this.collectVolume();
      this.collectMacd();
      this.collectRsi();
      this.collectObv();
      this.stampCandleValues();
      if (this.points.length === 0) {
        return;
      }
      this.setupChart();
    }
    onDisconnected() {
    }
    // host(컴포넌트) 크기 변경 시 재그리기
    onHostResize(matchedEls, entries) {
      if (this.chartCanvas)
        this.drawChart();
    }
    collectMas() {
      const mas = [];
      this.querySelectorAll(":scope > ma").forEach((el) => {
        const color = el.getAttribute("color") || "#6366f1";
        const sizeAttr = el.getAttribute("size") || el.getAttribute("period") || "20";
        const period = Math.max(2, Number(sizeAttr) || 20);
        const type = (el.getAttribute("type") || "sma").toLowerCase() === "exponential" ? "ema" : "sma";
        mas.push({ color, period, type });
      });
      this.mas = mas;
      this.seriesDirty = true;
    }
    collectLineSeries() {
      const out = [];
      this.querySelectorAll(":scope > series").forEach((el) => {
        const values = (el.getAttribute("values") || "").split(/[\s,]+/).map(Number).filter((v) => Number.isFinite(v));
        if (!values.length)
          return;
        const anchorRaw = (el.getAttribute("anchor") || "after-last").trim();
        let anchor = { type: "after-last" };
        const mAt = anchorRaw.match(/^at:(\d+)$/);
        if (mAt)
          anchor = { type: "at", index: Math.max(0, parseInt(mAt[1], 10)) };
        const dash = (el.getAttribute("dash") || "").split(/[\s,]+/).map(Number).filter((v) => Number.isFinite(v) && v >= 0);
        const extendRaw = Number(el.getAttribute("extend"));
        out.push({
          values,
          color: el.getAttribute("color") || "#8b5cf6",
          dash: dash.length ? dash : [5, 4],
          width: Math.max(0.5, Number(el.getAttribute("width")) || 1.5),
          label: el.getAttribute("label") || void 0,
          anchor,
          extend: anchor.type === "after-last" ? Number.isFinite(extendRaw) && extendRaw > 0 ? Math.floor(extendRaw) : values.length : 0
        });
      });
      this.lineSeries = out;
    }
    // ---------- 데이터 수집 ----------
    collectFromCandles() {
      const candleEls = this.querySelectorAll(":scope > candle");
      const points = [];
      candleEls.forEach((candleEl) => {
        const date = candleEl.getAttribute("date") || "";
        const open = Number(candleEl.getAttribute("open")) || 0;
        const high = Number(candleEl.getAttribute("high")) || 0;
        const low = Number(candleEl.getAttribute("low")) || 0;
        const close = Number(candleEl.getAttribute("close")) || 0;
        const volume = Number(candleEl.getAttribute("volume")) || 0;
        const lines = [];
        candleEl.querySelectorAll(":scope > line").forEach((lineEl) => {
          const w2 = lineEl.getAttribute("width");
          const c = lineEl.getAttribute("color");
          const t = lineEl.getAttribute("target");
          if (w2 || c || t)
            lines.push({ width: w2 ? Number(w2) || 1 : 1, color: c || "rgba(99,102,241,0.6)", ...t ? { target: t } : {} });
        });
        const tooltips = [];
        candleEl.querySelectorAll(":scope > tooltip").forEach((tipEl) => {
          let pos = tipEl.getAttribute("position") || "top";
          if (pos === "tip-top")
            pos = "candle-top";
          if (pos === "tip-bottom")
            pos = "candle-bottom";
          const lab = tipEl.getAttribute("label") || tipEl.getAttribute("lable") || "";
          const fillC = tipEl.getAttribute("fill-color") || tipEl.getAttribute("color") || "";
          const lineC = tipEl.getAttribute("line-color") || "";
          const labelC = tipEl.getAttribute("label-color") || "";
          const lw = tipEl.getAttribute("line-width");
          const col = fillC || labelC || lineC || "#6366f1";
          tooltips.push({
            position: ["top", "bottom", "candle-top", "candle-bottom"].includes(pos) ? pos : "top",
            color: col,
            fillColor: fillC || void 0,
            lineColor: lineC || void 0,
            labelColor: labelC || void 0,
            label: lab,
            lineWidth: lw ? Number(lw) : void 0
          });
        });
        const line = lines[0];
        const tooltip = tooltips[0];
        points.push({ date, open, high, low, close, volume, lines: lines.length ? lines : void 0, tooltips: tooltips.length ? tooltips : void 0, line, tooltip });
      });
      this.points = points;
      this.seriesDirty = true;
    }
    // 자식 <rect>/<arc> 요소를 도형으로 수집 (캔버스 픽셀 좌표 오버레이)
    collectFromShapes() {
      const shapes = [];
      this.querySelectorAll(":scope > rect").forEach((el) => {
        const attr = (n) => el.getAttribute(n);
        const dashRaw = attr("stroke-dasharray") || attr("dash") || void 0;
        const dash = dashRaw ? dashRaw.split(/[\s,]+/).map(Number).filter((v) => Number.isFinite(v) && v >= 0) : void 0;
        const base = { fill: attr("fill") || attr("fill-style") || void 0, stroke: attr("stroke") || attr("stroke-style") || void 0, strokeWidth: num(attr("stroke-width")), dash: dash?.length ? dash : void 0, target: attr("target") === "volume" ? "volume" : attr("target") === "all" ? "all" : "candle", marginTop: Math.max(0, num(attr("margin-top"))), marginBottom: Math.max(0, num(attr("margin-bottom"))), label: attr("label") || void 0, color: attr("color") || void 0 };
        const ds = attr("date-start"), de = attr("date-end");
        if (ds && de) {
          shapes.push({ type: "rect-date", dateStart: ds, dateEnd: de, ...base });
          return;
        }
        const sx = num(attr("start-x")), sy = num(attr("start-y"));
        const ex = num(attr("end-x")), ey = num(attr("end-y"));
        const x = sx || num(attr("x"));
        const y = sy || num(attr("y"));
        const width = ex && sx ? ex - sx : num(attr("width"));
        const height = ey && sy ? ey - sy : num(attr("height"));
        shapes.push({ type: "rect", x, y, width, height, ...base });
      });
      this.querySelectorAll(":scope > arc").forEach((el) => {
        const attr = (n) => el.getAttribute(n);
        shapes.push({
          type: "arc",
          x: num(attr("x")),
          y: num(attr("y")),
          r: num(attr("r") || attr("radius")),
          start: num(attr("start") || attr("start-angle")),
          end: num(attr("end") || attr("end-angle")),
          fill: attr("fill") || attr("fill-style") || void 0,
          stroke: attr("stroke") || attr("stroke-style") || void 0,
          strokeWidth: num(attr("stroke-width"))
        });
      });
      this.shapes = shapes;
    }
    // candle/ma/series 자식 요소 변경(추가/삭제/속성) 시 재수집 후 다시 그림
    // 셀렉터 생략 → $this(host, light DOM) observe
    onCandlesMutated(matchedEls) {
      this.collectFromCandles();
      this.collectFromShapes();
      this.collectMas();
      this.collectLineSeries();
      this.collectVolume();
      this.collectMacd();
      this.collectRsi();
      this.collectObv();
      this.stampCandleValues();
      if (this.chartCanvas && this.points.length > 0) {
        if (!this.viewInitDone || this.points.length !== this.lastPointsLength) {
          const n = this.points.length;
          this.viewStart = 0;
          this.viewEnd = n - 1;
          this.viewInitDone = true;
          this.lastPointsLength = n;
        }
        this.drawChart();
      }
    }
    setupChart() {
      const canvas = this.chartCanvas;
      if (!canvas || this.points.length === 0)
        return;
      if (!this.viewInitDone) {
        const n = this.points.length;
        this.viewStart = 0;
        this.viewEnd = n - 1;
        this.viewInitDone = true;
      }
      this.drawChart();
    }
    clampView() {
      const n = this.points.length;
      if (n === 0)
        return;
      let span = this.viewEnd - this.viewStart + 1;
      span = Math.max(8, Math.min(n, Math.round(span)));
      this.viewStart = Math.max(0, Math.min(this.viewStart, n - span));
      this.viewEnd = this.viewStart + span - 1;
      const atStart = this.viewStart <= 0;
      const atEnd = this.viewEnd >= n - 1;
      if (atStart && !this.edgeFired.start) {
        this.edgeFired.start = true;
        this.dispatchEvent(new CustomEvent("view-edge", { detail: { edge: "start" }, bubbles: true, composed: true }));
      } else if (!atStart) {
        this.edgeFired.start = false;
      }
      if (atEnd && !this.edgeFired.end) {
        this.edgeFired.end = true;
        this.dispatchEvent(new CustomEvent("view-edge", { detail: { edge: "end" }, bubbles: true, composed: true }));
      } else if (!atEnd) {
        this.edgeFired.end = false;
      }
    }
    isAtFullView() {
      const n = this.points.length;
      if (n === 0)
        return true;
      const span = this.viewEnd - this.viewStart + 1;
      return span >= n - 0.5;
    }
    /** 우측 끝 가시 시 제네릭 꺾은선 미래 슬롯 수 (그리기·히트테스팅 공용, 최대 12).
     *  after-last extend + at 앵커 꼬리(마지막봉 초과분) 모두 반영. */
    futurePadFor(s, eI) {
      if (eI < this.points.length - 1 || !this.lineSeries.length)
        return 0;
      const n = this.points.length;
      let m = 0;
      for (const ls of this.lineSeries) {
        const a = ls.anchor;
        if (a.type === "after-last")
          m = Math.max(m, ls.extend);
        else
          m = Math.max(m, a.index + ls.values.length - n);
      }
      return Math.min(12, Math.max(0, m));
    }
    onWheel(e) {
      if (this.disabledEvent)
        return;
      if (!this.controlsEnabled)
        return;
      const n = this.points.length;
      const span0 = this.viewEnd - this.viewStart + 1;
      const isFull = n > 0 && span0 >= n - 0.5;
      const factor = e.deltaY > 0 ? 1.15 : 1 / 1.15;
      if (isFull && factor > 1)
        return;
      e.preventDefault();
      const canvas = this.chartCanvas;
      if (!canvas)
        return;
      const rect = canvas.getBoundingClientRect();
      const plotW = Math.max(1, rect.width - this.padL - this.padR);
      const frac = Math.max(0, Math.min(1, (e.clientX - rect.left - this.padL) / plotW));
      const span = this.viewEnd - this.viewStart + 1;
      const anchorIdx = this.viewStart + frac * span;
      const newSpan = span * factor;
      this.viewStart = anchorIdx - frac * newSpan;
      this.viewEnd = this.viewStart + newSpan - 1;
      this.clampView();
      this.drawChart();
    }
    onMouseDown(e) {
      if (this.disabledEvent)
        return;
      if (!this.controlsEnabled && !this.readoutEnabled)
        return;
      this.dragging = true;
      this.dragLastX = e.clientX;
      this.downX = e.clientX;
      this.downY = e.clientY;
    }
    onMouseUp(e) {
      if (this.disabledEvent)
        return;
      if (!this.dragging)
        return;
      if (Date.now() - this.lastTouchEndTime < 350)
        return;
      this.dragging = false;
      const moved = Math.abs(e.clientX - this.downX) + Math.abs(e.clientY - this.downY);
      if (this.readoutEnabled && moved < 5) {
        const idx = this.indexAtX(e.clientX);
        this.selectedIdx = idx === this.selectedIdx ? -1 : idx;
        this.drawChart();
      }
    }
    onMouseMove(e) {
      if (this.disabledEvent)
        return;
      if (this.controlsEnabled && this.dragging) {
        const canvas = this.chartCanvas;
        if (!canvas)
          return;
        const rect = canvas.getBoundingClientRect();
        const plotW = Math.max(1, rect.width - this.padL - this.padR);
        const dataLen = Math.ceil(this.viewEnd) - Math.floor(this.viewStart) + 1;
        const candleW = plotW / dataLen;
        const dx = e.clientX - this.dragLastX;
        if (Math.abs(dx) >= 1) {
          const shift = -dx / candleW;
          this.viewStart += shift;
          this.viewEnd += shift;
          this.clampView();
          this.dragLastX = e.clientX;
          this.drawChart();
        }
      }
    }
    indexAtX(clientX) {
      const canvas = this.chartCanvas;
      if (!canvas)
        return -1;
      const rect = canvas.getBoundingClientRect();
      const padR = this.hiddenYLabel ? 8 : this.padR;
      const plotW = Math.max(1, rect.width - this.padL - padR);
      const s = Math.floor(this.viewStart), eI = Math.ceil(this.viewEnd);
      const xSpan = eI - s + 1 + this.futurePadFor(s, eI);
      const frac = (clientX - rect.left - this.padL) / plotW;
      const idx = Math.round(s + frac * xSpan - 0.5);
      return idx >= s && idx <= eI ? idx : -1;
    }
    onTouchStart(e) {
      if (this.disabledEvent)
        return;
      if (!this.controlsEnabled && !this.readoutEnabled)
        return;
      const touches = e.touches;
      if (touches.length === 2) {
        e.preventDefault();
        this.pinchDist0 = Math.abs(touches[0].clientX - touches[1].clientX);
        this.pinchSpan0 = this.viewEnd - this.viewStart + 1;
        this.pinchViewStart0 = this.viewStart;
        const canvas = this.chartCanvas;
        if (canvas) {
          const rect = canvas.getBoundingClientRect();
          const plotW = Math.max(1, rect.width - this.padL - this.padR);
          const midX = (touches[0].clientX + touches[1].clientX) / 2;
          this.pinchAnchorFrac = Math.max(0, Math.min(1, (midX - rect.left - this.padL) / plotW));
        }
        this.dragging = false;
      } else if (touches.length === 1) {
        this.dragging = true;
        this.dragLastX = touches[0].clientX;
        this.downX = touches[0].clientX;
        this.downY = touches[0].clientY;
      }
    }
    onTouchMove(e) {
      if (this.disabledEvent)
        return;
      if (!this.controlsEnabled)
        return;
      const touches = e.touches;
      if (touches.length === 2 && this.pinchDist0 > 0) {
        const dist = Math.abs(touches[0].clientX - touches[1].clientX);
        if (dist === 0)
          return;
        const factor = this.pinchDist0 / dist;
        const isFull = this.isAtFullView();
        if (isFull && factor > 1)
          return;
        e.preventDefault();
        const newSpan = this.pinchSpan0 * factor;
        const canvas = this.chartCanvas;
        const rect = canvas?.getBoundingClientRect();
        const plotW = rect ? Math.max(1, rect.width - this.padL - this.padR) : 1;
        const midX = (touches[0].clientX + touches[1].clientX) / 2;
        const curFrac = rect ? Math.max(0, Math.min(1, (midX - rect.left - this.padL) / plotW)) : this.pinchAnchorFrac;
        const anchorIdx = this.pinchSpan0 * curFrac + this.pinchViewStart0;
        this.viewStart = anchorIdx - curFrac * newSpan;
        this.viewEnd = this.viewStart + newSpan - 1;
        this.clampView();
        this.drawChart();
      } else if (touches.length === 1 && this.dragging) {
        if (this.isAtFullView())
          return;
        const canvas = this.chartCanvas;
        if (!canvas)
          return;
        const rect = canvas.getBoundingClientRect();
        const plotW = Math.max(1, rect.width - this.padL - this.padR);
        const dataLen = Math.ceil(this.viewEnd) - Math.floor(this.viewStart) + 1;
        const candleW = plotW / dataLen;
        const dx = touches[0].clientX - this.dragLastX;
        const dy = touches[0].clientY - this.downY;
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) >= 1) {
          e.preventDefault();
          this.viewStart += -dx / candleW;
          this.viewEnd += -dx / candleW;
          this.clampView();
          this.dragLastX = touches[0].clientX;
          this.drawChart();
        }
      }
    }
    onTouchEnd(e) {
      if (this.disabledEvent)
        return;
      const moved = Math.abs(e.changedTouches[0].clientX - this.downX) + Math.abs(e.changedTouches[0].clientY - this.downY);
      const wasPinch = this.pinchDist0 > 0;
      this.pinchDist0 = 0;
      if (this.readoutEnabled && this.dragging && !wasPinch && moved < 10) {
        const idx = this.indexAtX(e.changedTouches[0].clientX);
        this.selectedIdx = idx === this.selectedIdx ? -1 : idx;
        this.drawChart();
      }
      this.dragging = false;
      this.lastTouchEndTime = Date.now();
    }
    drawChart() {
      const canvas = this.chartCanvas;
      if (!canvas || this.points.length === 0)
        return;
      const cssW = canvas.clientWidth || 320;
      const cssH = canvas.clientHeight || 300;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.round(cssW * dpr);
      canvas.height = Math.round(cssH * dpr);
      const ctx = canvas.getContext("2d");
      if (!ctx)
        return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const startI = Math.floor(this.viewStart);
      const endI = Math.ceil(this.viewEnd);
      const data = this.points.slice(startI, endI + 1);
      if (data.length === 0)
        return;
      const W = cssW, H = cssH;
      const padL = this.padL, padT = 26;
      const padR = this.hiddenYLabel ? 8 : this.padR;
      const axisH = this.hiddenXLabel ? 6 : 18;
      const showMacd = !!this.macd;
      const showRsi = !!this.rsi;
      const showObv = !!this.obv;
      const gapV = this.showVolume ? 4 : 0;
      const gapM = showMacd ? 4 : 0;
      const gapR = showRsi ? 4 : 0;
      const gapO = showObv ? 4 : 0;
      const plotH = H - padT - axisH - gapV - gapM - gapR - gapO;
      const nSub = (this.showVolume ? 1 : 0) + (showMacd ? 1 : 0) + (showRsi ? 1 : 0) + (showObv ? 1 : 0);
      const frac = (a, b, c, d) => nSub <= 1 ? a : nSub === 2 ? b : nSub === 3 ? c : d;
      const volH = this.showVolume ? Math.max(8, plotH * frac(0.3, 0.2, 0.15, 0.12)) : 0;
      const macdH = showMacd ? Math.max(24, plotH * frac(0.28, 0.22, 0.18, 0.15)) : 0;
      const rsiH = showRsi ? Math.max(24, plotH * frac(0.28, 0.2, 0.16, 0.14)) : 0;
      const obvH = showObv ? Math.max(24, plotH * frac(0.28, 0.2, 0.16, 0.14)) : 0;
      const priceH = plotH - volH - macdH - rsiH - obvH;
      const priceBottom = padT + priceH;
      const plotW = W - padL - padR;
      const futurePad = this.futurePadFor(startI, endI);
      const xSpan = data.length + futurePad;
      const futureW = futurePad > 0 ? futurePad / xSpan * plotW : 0;
      const UP = "#e5484d", DOWN = "#3e63dd";
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, W, H);
      let maxP = -Infinity, minP = Infinity;
      let maxV = 0;
      for (const d of data) {
        maxP = Math.max(maxP, d.high);
        minP = Math.min(minP, d.low);
        maxV = Math.max(maxV, d.volume);
      }
      for (const ls of this.lineSeries) {
        const anchor = ls.anchor;
        if (anchor.type === "after-last") {
          if (endI >= this.points.length - 1) {
            for (const v of ls.values) {
              if (v > maxP)
                maxP = v;
              if (v < minP)
                minP = v;
            }
          }
          continue;
        }
        ls.values.forEach((v, k) => {
          const g = anchor.index + k;
          const hi = endI >= this.points.length - 1 ? this.points.length - 1 + futurePad : endI;
          if (g >= startI && g <= hi) {
            if (v > maxP)
              maxP = v;
            if (v < minP)
              minP = v;
          }
        });
      }
      const pPad = (maxP - minP) * 0.07 || maxP * 0.02 || 1;
      maxP += pPad;
      minP -= pPad;
      const yPrice = (p) => padT + priceH - (p - minP) / (maxP - minP) * priceH;
      const xCandle = (i) => padL + (i + 0.5) / xSpan * plotW;
      const candleW = plotW / xSpan;
      const bodyW = Math.max(1, Math.min(18, candleW * 0.65));
      ctx.font = "10px -apple-system, sans-serif";
      ctx.textBaseline = "middle";
      const gridCount = 4;
      for (let g = 0; g <= gridCount; g++) {
        const p = minP + (maxP - minP) * g / gridCount;
        const y = yPrice(p);
        ctx.strokeStyle = "#f0f2f5";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(padL, y);
        ctx.lineTo(W - padR, y);
        ctx.stroke();
        if (!this.hiddenYLabel) {
          ctx.fillStyle = "#98a2b3";
          ctx.textAlign = "left";
          ctx.fillText(p.toLocaleString(), W - padR + 6, y);
        }
      }
      const volTop = priceBottom + gapV;
      const macdTop = volTop + volH + gapM;
      const rsiTop = macdTop + macdH + gapR;
      const obvTop = rsiTop + rsiH + gapO;
      const volPlotH = Math.max(0, volH - 6);
      if (this.showVolume) {
        const fmtVol = (v) => {
          if (v >= 1e8)
            return (v / 1e8).toFixed(1) + "\uC5B5";
          if (v >= 1e4)
            return Math.round(v / 1e4).toLocaleString() + "\uB9CC";
          return v.toLocaleString();
        };
        ctx.strokeStyle = "#f6f7f9";
        for (let g = 1; g <= 2; g++) {
          const vv = maxV * g / 2;
          const vy = volTop + volH - volPlotH * g / 2;
          ctx.beginPath();
          ctx.moveTo(padL, vy);
          ctx.lineTo(W - padR, vy);
          ctx.stroke();
          if (!this.hiddenYLabel) {
            ctx.fillStyle = "#98a2b3";
            ctx.textAlign = "left";
            ctx.fillText(fmtVol(vv), W - padR + 6, vy);
          }
        }
        ctx.strokeStyle = "#f0f2f5";
        ctx.beginPath();
        ctx.moveTo(padL, volTop);
        ctx.lineTo(W - padR, volTop);
        ctx.stroke();
      }
      const isMobile = W < 420;
      const timeGridCount = isMobile ? 4 : 6;
      const step = Math.max(1, Math.ceil(data.length / timeGridCount));
      ctx.strokeStyle = "#f6f7f9";
      ctx.lineWidth = 1;
      for (let i = 0; i < data.length; i += step) {
        const gx = Math.round(xCandle(i)) + 0.5;
        ctx.beginPath();
        ctx.moveTo(gx, padT);
        ctx.lineTo(gx, H - axisH);
        ctx.stroke();
      }
      ctx.save();
      ctx.beginPath();
      ctx.rect(padL, padT, plotW + futureW, priceH);
      ctx.clip();
      for (let i = 0; i < data.length; i++) {
        const d = data[i];
        const up = d.close >= d.open;
        const color = up ? UP : DOWN;
        const x = Math.round(xCandle(i)) + 0.5;
        ctx.strokeStyle = color;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x, yPrice(d.high));
        ctx.lineTo(x, yPrice(d.low));
        ctx.stroke();
        const yO = yPrice(d.open), yC = yPrice(d.close);
        const top = Math.min(yO, yC);
        const hgt = Math.max(1, Math.abs(yC - yO));
        ctx.fillStyle = color;
        ctx.fillRect(x - bodyW / 2, top, bodyW, hgt);
      }
      if (this.mas.length) {
        const sma = this.ensureSeries().sma;
        for (const ma of this.mas) {
          const arr = sma.get(maSeriesKey(ma));
          if (!arr)
            continue;
          ctx.strokeStyle = ma.color;
          ctx.lineWidth = 1.2;
          ctx.lineJoin = "round";
          ctx.beginPath();
          let started = false;
          for (let i = 0; i < data.length; i++) {
            const v = arr[startI + i];
            if (v == null)
              continue;
            const px = xCandle(i);
            const py = yPrice(v);
            if (!started) {
              ctx.moveTo(px, py);
              started = true;
            } else
              ctx.lineTo(px, py);
          }
          ctx.stroke();
        }
      }
      for (const ls of this.lineSeries) {
        const anchor = ls.anchor;
        const pts = [];
        if (anchor.type === "after-last") {
          if (endI < this.points.length - 1)
            continue;
          const lastClose = this.points[this.points.length - 1]?.close;
          if (lastClose == null)
            continue;
          const i0 = this.points.length - 1 - startI;
          pts.push({ i: i0, v: lastClose });
          ls.values.forEach((v, k) => pts.push({ i: i0 + 1 + k, v }));
        } else {
          const atIdx = anchor.index;
          if (atIdx - 1 >= 0 && atIdx - 1 >= startI) {
            const prev = this.points[atIdx - 1]?.close;
            if (prev != null)
              pts.push({ i: atIdx - 1 - startI, v: prev });
          }
          ls.values.forEach((v, k) => pts.push({ i: atIdx + k - startI, v }));
        }
        const vis = pts.filter((p) => p.i >= -1 && p.i <= data.length + futurePad);
        if (!vis.length)
          continue;
        ctx.strokeStyle = ls.color;
        ctx.lineWidth = ls.width;
        ctx.lineJoin = "round";
        ctx.setLineDash(ls.dash);
        ctx.beginPath();
        vis.forEach((p, j) => {
          const px = xCandle(p.i);
          const py = yPrice(p.v);
          if (j === 0)
            ctx.moveTo(px, py);
          else
            ctx.lineTo(px, py);
        });
        ctx.stroke();
        ctx.setLineDash([]);
        if (ls.label) {
          const end = vis[vis.length - 1];
          const ex = xCandle(end.i);
          const ey = yPrice(end.v);
          ctx.font = "10px -apple-system, sans-serif";
          ctx.textBaseline = "middle";
          const tagW = ctx.measureText(ls.label).width + 12;
          const tx = Math.max(padL + 2, Math.min(W - tagW - 2, ex + 6));
          const ty = Math.max(padT + 8, Math.min(priceBottom - 8, ey - 12));
          ctx.fillStyle = ls.color;
          ctx.beginPath();
          ctx.roundRect(tx, ty - 8, tagW, 16, 4);
          ctx.fill();
          ctx.fillStyle = "#ffffff";
          ctx.fillText(ls.label, tx + 6, ty);
        }
      }
      ctx.restore();
      if (this.showVolume) {
        for (let i = 0; i < data.length; i++) {
          const d = data[i];
          const up = d.close >= d.open;
          const color = up ? UP : DOWN;
          const x = Math.round(xCandle(i)) + 0.5;
          const vh = maxV > 0 ? d.volume / maxV * (volH - 6) : 0;
          ctx.globalAlpha = 0.55;
          ctx.fillStyle = color;
          ctx.fillRect(x - bodyW / 2, volTop + volH - vh, bodyW, vh);
          ctx.globalAlpha = 1;
        }
      }
      if (showMacd && this.macd) {
        const m = this.macd;
        const full = computeMacdSeries(this.points.map((p) => p.close), m.fast, m.slow, m.signal);
        this.macdCache = full;
        const mM = full.macd.slice(startI, endI + 1);
        const mS = full.signal.slice(startI, endI + 1);
        const mH = full.hist.slice(startI, endI + 1);
        let mx = 0, mn = 0;
        for (let i = 0; i < data.length; i++) {
          mx = Math.max(mx, mM[i], mS[i], mH[i]);
          mn = Math.min(mn, mM[i], mS[i], mH[i]);
        }
        const mPad = (mx - mn) * 0.1 || 1;
        mx += mPad;
        mn -= mPad;
        const yM = (v) => macdTop + macdH - (v - mn) / (mx - mn) * macdH;
        const y0 = yM(0);
        ctx.save();
        ctx.beginPath();
        ctx.rect(padL, macdTop, plotW, macdH);
        ctx.clip();
        ctx.strokeStyle = "#cbd5e1";
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 3]);
        ctx.beginPath();
        ctx.moveTo(padL, y0);
        ctx.lineTo(W - padR, y0);
        ctx.stroke();
        ctx.setLineDash([]);
        for (let i = 0; i < data.length; i++) {
          const h = mH[i];
          const y1 = yM(h);
          const x = Math.round(xCandle(i)) + 0.5;
          ctx.globalAlpha = 0.5;
          ctx.fillStyle = h >= 0 ? UP : DOWN;
          ctx.fillRect(x - bodyW / 2, Math.min(y0, y1), bodyW, Math.max(1, Math.abs(y1 - y0)));
          ctx.globalAlpha = 1;
        }
        const strokeMacdLine = (arr, color) => {
          ctx.strokeStyle = color;
          ctx.lineWidth = 1.2;
          ctx.lineJoin = "round";
          ctx.beginPath();
          let started = false;
          for (let i = 0; i < data.length; i++) {
            const px = xCandle(i);
            const py = yM(arr[i]);
            if (!started) {
              ctx.moveTo(px, py);
              started = true;
            } else
              ctx.lineTo(px, py);
          }
          ctx.stroke();
        };
        strokeMacdLine(mM, m.fastColor);
        strokeMacdLine(mS, m.signalColor);
        ctx.restore();
        if (!this.hiddenYLabel) {
          ctx.fillStyle = "#98a2b3";
          ctx.textAlign = "left";
          ctx.textBaseline = "middle";
          ctx.fillText(fmtInd(mx - mPad), W - padR + 6, macdTop + 7);
          ctx.fillText(fmtInd(mn + mPad), W - padR + 6, macdTop + macdH - 7);
        }
      } else {
        this.macdCache = null;
      }
      if (showRsi && this.rsi) {
        const rcfg = this.rsi;
        const full = computeRsiSeries(this.points.map((p) => p.close), rcfg.period);
        this.rsiCache = full;
        const rV = full.slice(startI, endI + 1);
        const yR = (v) => rsiTop + rsiH - Math.max(0, Math.min(100, v)) / 100 * rsiH;
        ctx.save();
        ctx.beginPath();
        ctx.rect(padL, rsiTop, plotW, rsiH);
        ctx.clip();
        ctx.globalAlpha = 0.08;
        ctx.fillStyle = rcfg.overbought.color;
        ctx.fillRect(padL, yR(100), plotW, yR(rcfg.overbought.level) - yR(100));
        ctx.fillStyle = rcfg.oversold.color;
        ctx.fillRect(padL, yR(rcfg.oversold.level), plotW, yR(0) - yR(rcfg.oversold.level));
        ctx.globalAlpha = 1;
        const gridRsi = (v, color, dash) => {
          ctx.strokeStyle = color;
          ctx.lineWidth = 1;
          ctx.setLineDash(dash);
          ctx.beginPath();
          ctx.moveTo(padL, yR(v));
          ctx.lineTo(W - padR, yR(v));
          ctx.stroke();
          ctx.setLineDash([]);
        };
        gridRsi(rcfg.overbought.level, rcfg.overbought.color, [3, 3]);
        gridRsi(rcfg.oversold.level, rcfg.oversold.color, [3, 3]);
        gridRsi(50, "#cbd5e1", [3, 3]);
        ctx.strokeStyle = rcfg.lineColor;
        ctx.lineWidth = 1.2;
        ctx.lineJoin = "round";
        ctx.beginPath();
        let rStarted = false;
        for (let i = 0; i < data.length; i++) {
          const px = xCandle(i);
          const py = yR(rV[i]);
          if (!rStarted) {
            ctx.moveTo(px, py);
            rStarted = true;
          } else
            ctx.lineTo(px, py);
        }
        ctx.stroke();
        ctx.restore();
        if (!this.hiddenYLabel) {
          ctx.fillStyle = "#98a2b3";
          ctx.textAlign = "left";
          ctx.textBaseline = "middle";
          ctx.fillText(String(rcfg.overbought.level), W - padR + 6, yR(rcfg.overbought.level));
          ctx.fillText(String(rcfg.oversold.level), W - padR + 6, yR(rcfg.oversold.level));
        }
      } else {
        this.rsiCache = null;
      }
      if (showObv && this.obv) {
        const ocfg = this.obv;
        const full = computeObvSeries(this.points.map((p) => p.close), this.points.map((p) => p.volume));
        this.obvCache = full;
        const oV = full.slice(startI, endI + 1);
        let ox = -Infinity, on = Infinity;
        for (let i = 0; i < data.length; i++) {
          if (oV[i] > ox)
            ox = oV[i];
          if (oV[i] < on)
            on = oV[i];
        }
        const oPad = (ox - on) * 0.1 || Math.abs(ox) * 0.1 || 1;
        ox += oPad;
        on -= oPad;
        const yO = (v) => obvTop + obvH - (v - on) / (ox - on) * obvH;
        ctx.save();
        ctx.beginPath();
        ctx.rect(padL, obvTop, plotW, obvH);
        ctx.clip();
        ctx.strokeStyle = ocfg.lineColor;
        ctx.lineWidth = 1.2;
        ctx.lineJoin = "round";
        ctx.beginPath();
        let oStarted = false;
        for (let i = 0; i < data.length; i++) {
          const px = xCandle(i);
          const py = yO(oV[i]);
          if (!oStarted) {
            ctx.moveTo(px, py);
            oStarted = true;
          } else
            ctx.lineTo(px, py);
        }
        ctx.stroke();
        ctx.restore();
        if (!this.hiddenYLabel) {
          ctx.fillStyle = "#98a2b3";
          ctx.textAlign = "left";
          ctx.textBaseline = "middle";
          ctx.fillText(fmtBig(ox - oPad), W - padR + 6, obvTop + 7);
          ctx.fillText(fmtBig(on + oPad), W - padR + 6, obvTop + obvH - 7);
        }
      } else {
        this.obvCache = null;
      }
      for (let i = 0; i < data.length; i++) {
        const d = data[i];
        const x = xCandle(i);
        const lines = d.lines;
        const tooltips = d.tooltips;
        const lineList = lines ?? (d.line ? [d.line] : []);
        for (const ln of lineList) {
          const yEnd = ln.target === "all" ? H - axisH : padT + priceH;
          ctx.save();
          ctx.strokeStyle = ln.color;
          ctx.lineWidth = ln.width;
          ctx.beginPath();
          ctx.moveTo(Math.round(x) + 0.5, padT);
          ctx.lineTo(Math.round(x) + 0.5, yEnd);
          ctx.stroke();
          ctx.restore();
        }
        const tipList = tooltips ?? (d.tooltip ? [d.tooltip] : []);
        for (const tip of tipList) {
          if (!tip.label)
            continue;
          if (tip.lineWidth && tip.lineColor) {
            const yEnd = padT + priceH;
            ctx.save();
            ctx.strokeStyle = tip.lineColor;
            ctx.lineWidth = tip.lineWidth;
            ctx.beginPath();
            ctx.moveTo(Math.round(x) + 0.5, padT);
            ctx.lineTo(Math.round(x) + 0.5, yEnd);
            ctx.stroke();
            ctx.restore();
          }
          let ly = padT - 5;
          if (tip.position === "bottom")
            ly = padT + priceH + 6;
          else if (tip.position === "candle-top" || tip.position === "tip-top")
            ly = yPrice(d.high) - 10 - 8;
          else if (tip.position === "candle-bottom" || tip.position === "tip-bottom")
            ly = yPrice(d.low) + 10 + 8;
          const tipAny = tip;
          const fillColor = tipAny.fillColor;
          const labelColor = tipAny.labelColor;
          const hasFill = !!fillColor;
          const bg = fillColor || tip.color;
          const fg = labelColor || (tip.label === "D" || tip.label === "G" ? "#fff" : "#fff");
          const useTextOnly = !hasFill && !!labelColor;
          ctx.font = "bold 10px -apple-system, sans-serif";
          const tw = ctx.measureText(tip.label).width + 10;
          const th = 16;
          let tx = x - tw / 2;
          tx = Math.max(padL + 2, Math.min(W - padR - tw - 2, tx));
          if (ly - th / 2 < padT - 18)
            ly = padT - 18 + th / 2;
          if (ly + th / 2 > padT + priceH + 18)
            ly = padT + priceH + 18 - th / 2;
          if (hasFill) {
            ctx.fillStyle = bg;
            ctx.beginPath();
            ctx.roundRect(tx, ly - th / 2, tw, th, 6);
            ctx.fill();
            ctx.fillStyle = fg;
          } else {
            ctx.fillStyle = labelColor || tip.color;
          }
          if (hasFill) {
            ctx.strokeStyle = tip.label === "D" ? "#ef4444" : tip.label === "G" ? "#d97706" : tipAny.lineColor || bg;
            ctx.lineWidth = 1;
            ctx.stroke();
          }
          ctx.fillStyle = fg;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(tip.label, tx + tw / 2, ly + 0.5);
        }
      }
      if (this.showCloseLine) {
        ctx.strokeStyle = "#111111";
        ctx.lineWidth = 1;
        ctx.lineJoin = "round";
        ctx.beginPath();
        for (let i = 0; i < data.length; i++) {
          const px = xCandle(i);
          const py = yPrice(data[i].close);
          if (i === 0)
            ctx.moveTo(px, py);
          else
            ctx.lineTo(px, py);
        }
        ctx.stroke();
      }
      let hiIdx = 0, loIdx = 0;
      for (let i = 1; i < data.length; i++) {
        if (data[i].high > data[hiIdx].high)
          hiIdx = i;
        if (data[i].low < data[loIdx].low)
          loIdx = i;
      }
      const drawChartTip = (i, price, text, above) => {
        ctx.font = "bold 10px -apple-system, sans-serif";
        const tw = ctx.measureText(text).width + 14;
        const th = 16;
        const cx = Math.max(padL + tw / 2, Math.min(W - padR - tw / 2, xCandle(i)));
        const py = yPrice(price);
        const ty = above ? py - 8 - th : py + 8;
        ctx.fillStyle = "#33475b";
        ctx.beginPath();
        ctx.roundRect(cx - tw / 2, ty, tw, th, 4);
        ctx.fill();
        const tipX = Math.max(cx - tw / 2 + 6, Math.min(cx + tw / 2 - 6, xCandle(i)));
        ctx.beginPath();
        if (above) {
          ctx.moveTo(tipX - 4, ty + th);
          ctx.lineTo(tipX + 4, ty + th);
          ctx.lineTo(tipX, ty + th + 4);
        } else {
          ctx.moveTo(tipX - 4, ty);
          ctx.lineTo(tipX + 4, ty);
          ctx.lineTo(tipX, ty - 4);
        }
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = "#fff";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(text, cx, ty + th / 2 + 0.5);
      };
      if (this.showMinMax) {
        drawChartTip(hiIdx, data[hiIdx].high, `\uCD5C\uACE0: ${data[hiIdx].high.toLocaleString()}\uC6D0`, true);
        drawChartTip(loIdx, data[loIdx].low, `\uCD5C\uC800: ${data[loIdx].low.toLocaleString()}\uC6D0`, false);
      }
      if (!this.hiddenXLabel) {
        ctx.fillStyle = "#98a2b3";
        ctx.textAlign = "center";
        ctx.font = `${isMobile ? 9 : 10}px -apple-system, sans-serif`;
        for (let i = 0; i < data.length; i += step) {
          const label = data[i].date;
          const x = xCandle(i);
          if (x < padL + 20 || x > W - padR - 20)
            continue;
          ctx.fillText(label, x, H - axisH / 2);
        }
      }
      if (this.showLastLine) {
        const last = data[data.length - 1];
        const lastUp = last.close >= last.open;
        const ly = yPrice(last.close);
        ctx.strokeStyle = lastUp ? UP : DOWN;
        ctx.setLineDash([4, 3]);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(padL, ly);
        ctx.lineTo(W - padR, ly);
        ctx.stroke();
        ctx.setLineDash([]);
        const tagText = last.close.toLocaleString();
        ctx.fillStyle = lastUp ? UP : DOWN;
        const tagW = ctx.measureText(tagText).width + 10;
        ctx.beginPath();
        const tagY = Math.max(padT, Math.min(priceBottom - 8, ly));
        ctx.roundRect(W - padR + 2, tagY - 8, tagW, 16, 4);
        ctx.fill();
        ctx.fillStyle = "#fff";
        ctx.textAlign = "left";
        ctx.fillText(tagText, W - padR + 7, tagY);
      }
      {
        ctx.font = "10px -apple-system, sans-serif";
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        const rowH = 15, padBX = 5, lineW = 12, gapX = 9;
        const maxW = Math.max(40, plotW - 8);
        const drawPaneLegend = (top, items) => {
          if (!items.length)
            return;
          const rows = [[]];
          let cx = 0;
          for (const it of items) {
            const w2 = (it.color ? lineW + 4 : 0) + ctx.measureText(it.label).width;
            if (cx > 0 && cx + w2 > maxW) {
              rows.push([]);
              cx = 0;
            }
            rows[rows.length - 1].push({ ...it, x: cx, w: w2 });
            cx += w2 + gapX;
          }
          const bgW = Math.max(...rows.map((r) => r.reduce((a, b) => a + b.w + gapX, -gapX)));
          const bx = padL + 2, by = top + 2;
          ctx.fillStyle = "rgba(255,255,255,0.88)";
          ctx.beginPath();
          ctx.roundRect(bx, by, bgW + padBX * 2, rows.length * rowH + 6, 5);
          ctx.fill();
          ctx.strokeStyle = "#eef0f4";
          ctx.lineWidth = 1;
          ctx.stroke();
          rows.forEach((row, ri) => {
            const ry = by + 3 + ri * rowH + rowH / 2;
            for (const it of row) {
              const ix = bx + padBX + it.x;
              if (it.color) {
                ctx.strokeStyle = it.color;
                ctx.lineWidth = 2.5;
                ctx.beginPath();
                ctx.moveTo(ix, ry);
                ctx.lineTo(ix + lineW, ry);
                ctx.stroke();
              }
              ctx.fillStyle = "#475569";
              ctx.fillText(it.label, ix + (it.color ? lineW + 4 : 0), ry + 0.5);
            }
          });
        };
        if (this.mas.length)
          drawPaneLegend(padT, this.mas.map((ma) => ({ label: `MA${ma.period}`, color: ma.color })));
        if (this.showVolume)
          drawPaneLegend(volTop, [{ label: "\uAC70\uB798\uB7C9", color: null }]);
        if (this.macd)
          drawPaneLegend(macdTop, [{ label: "MACD", color: this.macd.fastColor }, { label: "SIG", color: this.macd.signalColor }]);
        if (this.rsi)
          drawPaneLegend(rsiTop, [{ label: `RSI${this.rsi.period}`, color: this.rsi.lineColor }]);
        if (this.obv)
          drawPaneLegend(obvTop, [{ label: "OBV", color: this.obv.lineColor }]);
      }
      const readout = this.readoutEl;
      if (this.selectedIdx >= startI && this.selectedIdx <= endI && readout) {
        const hi = this.selectedIdx - startI;
        const d = this.points[this.selectedIdx];
        const up = d.close >= d.open;
        const hx = Math.round(xCandle(hi)) + 0.5;
        ctx.strokeStyle = "#94a3b8";
        ctx.setLineDash([3, 3]);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(hx, padT);
        ctx.lineTo(hx, H - axisH);
        ctx.stroke();
        const cy = yPrice(d.close);
        ctx.strokeStyle = up ? UP : DOWN;
        ctx.beginPath();
        ctx.moveTo(padL, cy);
        ctx.lineTo(W - padR, cy);
        ctx.stroke();
        if (this.showVolume && d.volume > 0) {
          const vy = volTop + volH - d.volume / maxV * volPlotH;
          ctx.beginPath();
          ctx.moveTo(padL, vy);
          ctx.lineTo(W - padR, vy);
          ctx.stroke();
        }
        ctx.setLineDash([]);
        ctx.font = "bold 10px -apple-system, sans-serif";
        const selLabel = d.date.includes(" ") ? d.date : d.date.slice(5);
        const selTw = ctx.measureText(selLabel).width + 10;
        ctx.fillStyle = "#33475b";
        ctx.beginPath();
        ctx.roundRect(Math.min(W - padR - selTw / 2, Math.max(padL, hx - selTw / 2)), H - axisH + 2, selTw, axisH - 4, 4);
        ctx.fill();
        ctx.fillStyle = "#fff";
        ctx.textAlign = "center";
        ctx.fillText(selLabel, Math.min(W - padR - selTw / 2, Math.max(padL, hx - selTw / 2)) + selTw / 2, H - axisH / 2);
        const prev = this.points[this.selectedIdx - 1] || d;
        const diff = prev.close ? (d.close - prev.close) / prev.close * 100 : 0;
        const sign = diff > 0 ? "+" : "";
        readout.innerHTML = `
          <span>${d.date}</span>
          <span>\uC2DC ${d.open.toLocaleString()}</span>
          <span>\uACE0 ${d.high.toLocaleString()}</span>
          <span>\uC800 ${d.low.toLocaleString()}</span>
          <span style="color:${up ? UP : DOWN};font-weight:700">\uC885 ${d.close.toLocaleString()} (${sign}${diff.toFixed(2)}%)</span>
          <span>\uAC70\uB798\uB7C9 ${d.volume.toLocaleString()}</span>
          ${this.macd && this.macdCache && this.selectedIdx >= 0 && this.selectedIdx < this.macdCache.macd.length ? `<span style="color:${this.macd.fastColor};font-weight:700">MACD ${this.macdCache.macd[this.selectedIdx].toFixed(2)}</span><span style="color:${this.macd.signalColor};font-weight:700">\uC2DC\uADF8\uB110 ${this.macdCache.signal[this.selectedIdx].toFixed(2)}</span>` : ""}
          ${this.rsi && this.rsiCache && this.selectedIdx >= 0 && this.selectedIdx < this.rsiCache.length ? `<span style="color:${this.rsi.lineColor};font-weight:700">RSI ${this.rsiCache[this.selectedIdx].toFixed(1)}</span>` : ""}
          ${this.obv && this.obvCache && this.selectedIdx >= 0 && this.selectedIdx < this.obvCache.length ? `<span style="color:${this.obv.lineColor};font-weight:700">OBV ${Math.round(this.obvCache[this.selectedIdx]).toLocaleString()}</span>` : ""}
        `;
        readout.style.left = `${Math.max(4, hx - 60)}px`;
        readout.style.top = `${Math.max(4, yPrice(d.high) - 48)}px`;
        readout.style.display = "flex";
        requestAnimationFrame(() => {
          const r = readout.getBoundingClientRect();
          let lx = hx - r.width / 2;
          lx = Math.max(4, Math.min(cssW - r.width - 4, lx));
          let ty = yPrice(d.high) - r.height - 10;
          if (ty < padT)
            ty = yPrice(d.low) + 12;
          readout.style.left = `${lx}px`;
          readout.style.top = `${Math.max(4, ty)}px`;
        });
      } else if (readout) {
        readout.style.display = "none";
      }
      this.drawShapes(ctx, { data, xCandle, bodyW, yPrice, minP, maxP, volTop, volH, H, axisH, padT });
    }
    // rect 좌상단 라벨 (배경 pill + 텍스트)
    drawShapeLabel(ctx, x, y, label, color) {
      if (!label)
        return;
      ctx.font = "bold 10px -apple-system, sans-serif";
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      const tw = ctx.measureText(label).width;
      const lx = x + 4, ly = y + 4;
      ctx.fillStyle = "rgba(255,255,255,0.88)";
      ctx.beginPath();
      ctx.roundRect(lx - 3, ly - 2, tw + 12, 17, 5);
      ctx.fill();
      ctx.fillStyle = color || "#475569";
      ctx.fillText(label, lx + 3, ly + 1);
    }
    drawShapes(ctx, c) {
      for (const s of this.shapes) {
        ctx.save();
        if (s.fill)
          ctx.fillStyle = s.fill;
        if (s.stroke) {
          ctx.strokeStyle = s.stroke;
          ctx.lineWidth = s.strokeWidth || 1;
          ctx.setLineDash(s.dash ?? []);
        }
        if (s.type === "rect-date") {
          const g0 = this.points.findIndex((d) => d.date === s.dateStart);
          const g1 = this.points.findIndex((d) => d.date === s.dateEnd);
          if (g0 < 0 || g1 < 0 || g1 < g0)
            continue;
          const v0 = Math.floor(this.viewStart);
          const c0 = Math.max(g0, v0);
          const c1 = Math.min(g1, Math.ceil(this.viewEnd));
          if (c1 < c0)
            continue;
          const i0 = c0 - v0;
          const i1 = c1 - v0;
          const x = c.xCandle(i0) - c.bodyW / 2;
          const w2 = c.xCandle(i1) + c.bodyW / 2 - x;
          let y, h;
          if (s.target === "all") {
            y = c.yPrice(c.maxP);
            h = c.H - c.axisH - y;
          } else if (s.target === "volume") {
            y = c.volTop;
            h = c.volH;
          } else {
            y = c.yPrice(c.maxP);
            h = c.yPrice(c.minP) - y;
          }
          const mTop = Math.max(0, s.marginTop || 0), mBot = Math.max(0, s.marginBottom || 0);
          y += mTop;
          h = Math.max(0, h - mTop - mBot);
          if (s.fill)
            ctx.fillRect(x, y, w2, h);
          if (s.stroke)
            ctx.strokeRect(x, y, w2, h);
          this.drawShapeLabel(ctx, x, y, s.label, s.color);
        } else if (s.type === "rect") {
          const mTop = Math.max(0, s.marginTop || 0), mBot = Math.max(0, s.marginBottom || 0);
          const ry = s.y + mTop, rh = Math.max(0, s.height - mTop - mBot);
          if (s.fill)
            ctx.fillRect(s.x, ry, s.width, rh);
          if (s.stroke)
            ctx.strokeRect(s.x, ry, s.width, rh);
          this.drawShapeLabel(ctx, s.x, ry, s.label, s.color);
        } else if (s.type === "arc") {
          ctx.beginPath();
          ctx.arc(s.x, s.y, s.r, s.start, s.end);
          if (s.fill)
            ctx.fill();
          if (s.stroke)
            ctx.stroke();
        }
        ctx.restore();
      }
    }
  };
  __decorate([
    changedAttribute("show-close-line", { type: Boolean }),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Boolean]),
    __metadata("design:returntype", void 0)
  ], StockChartImpl.prototype, "onShowCloseLineChanged", null);
  __decorate([
    changedAttribute("show-min-max", { type: Boolean }),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Boolean]),
    __metadata("design:returntype", void 0)
  ], StockChartImpl.prototype, "onShowMinMaxChanged", null);
  __decorate([
    changedAttribute("show-last-line", { type: Boolean }),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Boolean]),
    __metadata("design:returntype", void 0)
  ], StockChartImpl.prototype, "onShowLastLineChanged", null);
  __decorate([
    changedAttribute("enabled-control", { type: Boolean }),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Boolean]),
    __metadata("design:returntype", void 0)
  ], StockChartImpl.prototype, "onEnabledControlChanged", null);
  __decorate([
    changedAttribute("enabled-readout", { type: Boolean }),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Boolean]),
    __metadata("design:returntype", void 0)
  ], StockChartImpl.prototype, "onEnabledReadoutChanged", null);
  __decorate([
    changedAttribute("hidden-x-label", { type: Boolean }),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Boolean]),
    __metadata("design:returntype", void 0)
  ], StockChartImpl.prototype, "onHiddenXLabelChanged", null);
  __decorate([
    changedAttribute("hidden-y-label", { type: Boolean }),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Boolean]),
    __metadata("design:returntype", void 0)
  ], StockChartImpl.prototype, "onHiddenYLabelChanged", null);
  __decorate([
    changedAttribute("disabled-event", { type: Boolean }),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Boolean]),
    __metadata("design:returntype", void 0)
  ], StockChartImpl.prototype, "onDisabledEventChanged", null);
  __decorate([
    onConnectedBodyShadow,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", String)
  ], StockChartImpl.prototype, "render", null);
  __decorate([
    onConnectedAfter,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
  ], StockChartImpl.prototype, "onConnected", null);
  __decorate([
    onDisconnected,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
  ], StockChartImpl.prototype, "onDisconnected", null);
  __decorate([
    resizeObserverLight,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Array, Array]),
    __metadata("design:returntype", void 0)
  ], StockChartImpl.prototype, "onHostResize", null);
  __decorate([
    mutationObserverLight({ childList: true, attributes: true, subtree: true }),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Array]),
    __metadata("design:returntype", void 0)
  ], StockChartImpl.prototype, "onCandlesMutated", null);
  __decorate([
    queryShadow("#stock-chart-canvas"),
    __metadata("design:type", typeof (_a = typeof HTMLCanvasElement !== "undefined" && HTMLCanvasElement) === "function" ? _a : Object)
  ], StockChartImpl.prototype, "chartCanvas", void 0);
  __decorate([
    queryShadow("#stock-chart-readout"),
    __metadata("design:type", typeof (_b = typeof HTMLElement !== "undefined" && HTMLElement) === "function" ? _b : Object)
  ], StockChartImpl.prototype, "readoutEl", void 0);
  __decorate([
    eventShadow("#stock-chart-canvas", "wheel", { passive: false }),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [typeof (_c = typeof WheelEvent !== "undefined" && WheelEvent) === "function" ? _c : Object]),
    __metadata("design:returntype", void 0)
  ], StockChartImpl.prototype, "onWheel", null);
  __decorate([
    eventShadow("#stock-chart-canvas", "mousedown"),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [typeof (_d = typeof MouseEvent !== "undefined" && MouseEvent) === "function" ? _d : Object]),
    __metadata("design:returntype", void 0)
  ], StockChartImpl.prototype, "onMouseDown", null);
  __decorate([
    eventWindow("mouseup"),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [typeof (_e = typeof MouseEvent !== "undefined" && MouseEvent) === "function" ? _e : Object]),
    __metadata("design:returntype", void 0)
  ], StockChartImpl.prototype, "onMouseUp", null);
  __decorate([
    eventShadow("#stock-chart-canvas", "mousemove"),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [typeof (_f = typeof MouseEvent !== "undefined" && MouseEvent) === "function" ? _f : Object]),
    __metadata("design:returntype", void 0)
  ], StockChartImpl.prototype, "onMouseMove", null);
  __decorate([
    eventShadow("#stock-chart-canvas", "touchstart", { passive: false }),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [typeof (_g = typeof TouchEvent !== "undefined" && TouchEvent) === "function" ? _g : Object]),
    __metadata("design:returntype", void 0)
  ], StockChartImpl.prototype, "onTouchStart", null);
  __decorate([
    eventShadow("#stock-chart-canvas", "touchmove", { passive: false }),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [typeof (_h = typeof TouchEvent !== "undefined" && TouchEvent) === "function" ? _h : Object]),
    __metadata("design:returntype", void 0)
  ], StockChartImpl.prototype, "onTouchMove", null);
  __decorate([
    eventShadow("#stock-chart-canvas", "touchend"),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [typeof (_j = typeof TouchEvent !== "undefined" && TouchEvent) === "function" ? _j : Object]),
    __metadata("design:returntype", void 0)
  ], StockChartImpl.prototype, "onTouchEnd", null);
  StockChartImpl = __decorate([
    elementDefine(tagName, { window: w })
  ], StockChartImpl);
  return StockChartImpl;
};
export {
  buildCandleInfo,
  StockChart_default as default,
  maSeriesKey
};
//# sourceMappingURL=StockChart.js.map
